import { useState, useCallback, useRef, useEffect } from 'react';
import { apiClient } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { useStreamingStatus } from '@/hooks/useStreamingStatus';
import type { Event, Content } from '@/types';

interface StreamingState {
  isStreaming: boolean;
  currentMessage: string;
  error: string | null;
  connectionStatus: 'connected' | 'connecting' | 'disconnected' | 'error';
}

interface UseStreamingOptions {
  onMessage?: (content: string, isComplete: boolean) => void;
  onComplete?: (events: Event[]) => void;
  onError?: (error: string) => void;
  maxRetries?: number;
  retryDelay?: number;
}

export function useStreaming(options: UseStreamingOptions = {}) {
  const {
    onMessage,
    onComplete,
    onError,
    maxRetries = 3,
    retryDelay = 1000,
  } = options;

  const { user } = useAppStore();
  const { setStreaming, setConnectionStatus, setError: setGlobalError } = useStreamingStatus();
  const [state, setState] = useState<StreamingState>({
    isStreaming: false,
    currentMessage: '',
    error: null,
    connectionStatus: 'disconnected',
  });

  const retryCountRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const updateState = useCallback((updates: Partial<StreamingState>) => {
    setState(prev => ({ ...prev, ...updates }));

    // Update global streaming status
    if ('isStreaming' in updates) {
      setStreaming(updates.isStreaming!);
    }
    if ('connectionStatus' in updates) {
      setConnectionStatus(updates.connectionStatus!);
    }
    if ('error' in updates) {
      setGlobalError(updates.error || null);
    }
  }, [setStreaming, setConnectionStatus, setGlobalError]);

  const streamMessage = useCallback(async (
    sessionId: string, 
    content: Content,
    toolConfig?: { corpus_ids: string[]; enable_google_search: boolean; model: string }
  ) => {
    if (!user.authenticated) {
      const error = 'User not authenticated';
      updateState({ error, connectionStatus: 'error' });
      onError?.(error);
      return;
    }

    // Cancel any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    updateState({
      isStreaming: true,
      currentMessage: '',
      error: null,
      connectionStatus: 'connecting',
    });

    try {
      const request = {
        sessionId,
        newMessage: content,
        streaming: false, // Turn off streaming
        toolConfig, // Add tool configuration
      };

      updateState({ connectionStatus: 'connected' });

      // Use the regular sendMessage method instead of streaming
      const response = await apiClient.sendMessage(request);

      // Check if we've been aborted
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      // Extract the complete response content
      let finalContent = '';
      for (const event of response.events) {
        if (event.content?.parts) {
          for (const part of event.content.parts) {
            if (part.text && !part.thought) {
              finalContent += part.text;
            }
          }
        }
      }

      updateState({ currentMessage: finalContent });
      onMessage?.(finalContent, false);

      // Request completed successfully
      updateState({
        isStreaming: false,
        connectionStatus: 'connected',
      });

      onMessage?.(finalContent, true);
      onComplete?.(response.events);
      retryCountRef.current = 0; // Reset retry count on success

    } catch (error) {
      if (abortControllerRef.current?.signal.aborted) {
        // Stream was intentionally cancelled
        updateState({
          isStreaming: false,
          connectionStatus: 'disconnected',
        });
        return;
      }

      const errorMessage = error instanceof Error ? error.message : 'Streaming failed';
      console.error('Streaming error:', error);

      // Implement exponential backoff retry logic
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        const delay = retryDelay * Math.pow(2, retryCountRef.current - 1);

        updateState({
          connectionStatus: 'connecting',
          error: `Connection lost. Retrying in ${delay / 1000}s... (${retryCountRef.current}/${maxRetries})`,
        });

        setTimeout(() => {
          if (!abortControllerRef.current?.signal.aborted) {
            streamMessage(sessionId, content);
          }
        }, delay);
      } else {
        updateState({
          isStreaming: false,
          connectionStatus: 'error',
          error: errorMessage,
        });
        onError?.(errorMessage);
        retryCountRef.current = 0;
      }
    }
  }, [user, maxRetries, retryDelay, onMessage, onComplete, onError, updateState]);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    updateState({
      isStreaming: false,
      connectionStatus: 'disconnected',
      error: null,
    });
  }, [updateState]);

  const resetError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    ...state,
    streamMessage,
    cancelStream,
    resetError,
  };
}